import { addQueryParamsToUri, getUri, patchUri, postUri } from '../utils/fetch';
import Env from '../utils/env';
import { VendorKeys } from "../constants/vendor";
import { IPaginationRequest } from '../interfaces/pagination';
import { IRpmManifestsResponse, IVulnerabilitiesResponse } from 'interfaces/vulnerability';
import { IPyxisImage } from 'interfaces/pyxisImage';
import { IProjectDetails, IProjectSecrets } from 'interfaces/project';
import { ProductKeys } from '../constants/products';
import  { mutation } from 'gql-query-builder'
import { IPageState } from '@rhc-shared-components/table-toolbar/dist/models/GeneralModels';
import { GQLMultiArchListResponse } from "../interfaces/multiarchgql";
import { IRepoDetailsResponse } from "../interfaces/repo";
import { IImageScanResultsResponse } from "../interfaces/imageScanResults";
import { IOperatorTestResultResponse } from "../interfaces/operatorTestResults";
import { IImageArtifacts } from "../interfaces/imageArtifacts";
import { IOperatorArtifactsResponse } from "../interfaces/operatorArtifacts";
import { IImageListGraphQLResponse } from "../interfaces/imagelistgraphql";

const AdditionalParams: RequestInit = {};

interface IAPIParams {
  filter?: string;
  sort_by?: any[];
}

/*
 *
 * Certification Projects APIs
 *
 */

// POSTs a project
export function createProject(projectData, abortSignal?: AbortSignal): Promise<any> {
  const uri = Env.pyxisAPIURL
      .clone()
      .setPath(`${Env.apiPrefix}/${Env.apiVersion}/projects/certification`);
  const params = abortSignal ? {'signal': abortSignal} : {};
  return postUri(uri, projectData, params);
}

// PATCHes the project details by id
export const patchProjectDetails = (id: string, projectDetails: any): Promise<any> => {
  const uri =  Env.pyxisAPIURL
    .clone()
    .setPath(`${Env.apiPrefix}/${Env.apiVersion}/projects/certification/id/${id}`);
  return patchUri(uri, projectDetails);
}

// GETs the project details by mongo id
export const getProjectDetailsById = (id: string, abortSignal?: AbortSignal): Promise<IProjectDetails> => {
  const uri =  Env.pyxisAPIURL
    .clone()
    .setPath(`${Env.apiPrefix}/${Env.apiVersion}/projects/certification/id/${id}`);
  const params = abortSignal ? {'signal': abortSignal} : {};
  return getUri(uri, params);
}

// GETs the project details by pid
export const getProjectDetailsByPid = (pid: string, abortSignal?: AbortSignal): Promise<any> => {
  const uri =  Env.pyxisAPIURL
    .clone()
    .setPath(`${Env.apiPrefix}/${Env.apiVersion}/projects/certification/pid/${pid}`);
  const params = abortSignal ? {'signal': abortSignal} : {};
  return getUri(uri, params);
}

// GETs test scan logs for a project
export const getImageTestScanLogDetails = (url: string): Promise<any> => {
  return getUri<any>(url);
}

// GETs the test results for operator project
export const getProjectTestResults = (pid: string, pagination?: IPaginationRequest, extraParams: IAPIParams = {}, abortSignal?: AbortSignal): Promise<any> => {
  const uri =  Env.pyxisAPIURL
    .clone()
    .setPath(`${Env.apiPrefix}/${Env.apiVersion}/projects/certification/id/${pid}/test-results`);

  addQueryParamsToUri(uri, pagination);
  addQueryParamsToUri(uri, extraParams);

  if (abortSignal) {
      // this will be coming from useFetch hook
      Object.assign(AdditionalParams, {'signal': abortSignal})
  }
  const params = abortSignal ? {'signal': abortSignal} : {};
  return getUri(uri, params);
}

// GETs the test result details for operator project
export const getProjectTestResultById = (id: string, abortSignal?: AbortSignal): Promise<IOperatorTestResultResponse> => {
  const uri =  Env.pyxisAPIURL
    .clone()
    .setPath(`${Env.apiPrefix}/${Env.apiVersion}/projects/certification/test-results/id/${id}`);
  const params = abortSignal ? {'signal': abortSignal} : {};
  return getUri(uri, params);
}

// GETs the artifacts for operator project
export const getProjectArtifacts = (pid: string, pagination?: IPaginationRequest, extraParams: IAPIParams = {}, abortSignal?: AbortSignal): Promise<IOperatorArtifactsResponse> => {
  const uri =  Env.pyxisAPIURL
    .clone()
    .setPath(`${Env.apiPrefix}/${Env.apiVersion}/projects/certification/id/${pid}/artifacts`);

  addQueryParamsToUri(uri, pagination);
  addQueryParamsToUri(uri, extraParams);

  if (abortSignal) {
    // this will be coming from useFetch hook
    Object.assign(AdditionalParams, {'signal': abortSignal})
  }
  const params = abortSignal ? {'signal': abortSignal} : {};
  return getUri(uri, params);
}

/*
 *
 * Certification Project Requests APIs
 *
 */

// POSTs the scan request for a project
export const requestProjectScan = (id: string, scanImageDetails: any): Promise<any> => {
  const uri =  Env.pyxisAPIURL
    .clone()
    .setPath(`${Env.apiPrefix}/${Env.apiVersion}/projects/certification/id/${id}/requests/scans`);
  return postUri(uri, scanImageDetails);
}

// POSTs the requests to create certification project build requests using its project ID
export function createProjectBuildRequests(id: string, buildTagDetails: any ,abortSignal?: AbortSignal): Promise<any> {
  const uri = Env.pyxisAPIURL
      .clone()
      .setPath(`${Env.apiPrefix}/${Env.apiVersion}/projects/certification/id/${id}/requests/builds`);
  const params = abortSignal ? {'signal': abortSignal} : {};
  return postUri(uri, buildTagDetails, params);
}

// GETs the certification project build requests using its project ID
export const getProjectCertificationBuildRequests = (pid: string, pagination?: IPaginationRequest, abortSignal?: AbortSignal): Promise<any> => {
  const uri =  Env.pyxisAPIURL
    .clone()
    .setPath(`${Env.apiPrefix}/${Env.apiVersion}/projects/certification/id/${pid}/requests/builds`);
  addQueryParamsToUri(uri, {
    sort_by: ['creation_date[desc]']
  })
  addQueryParamsToUri(uri, pagination);
  const params = abortSignal ? {'signal': abortSignal} : {};
  return getUri(uri, params);
}

// GETs the requests to get the details of the build using the build id
export const getProjectBuildDetails = (pid: string, abortSignal?: AbortSignal): Promise<any> => {
  const uri =  Env.pyxisAPIURL
    .clone()
    .setPath(`${Env.apiPrefix}/${Env.apiVersion}/projects/certification/requests/builds/id/${pid}`);
  const params = abortSignal ? {'signal': abortSignal} : {};
  return getUri(uri, params);
}

// GETs the requests to get the log details of the build using the build id
export const getProjectBuildLogs = (pid: string, abortSignal?: AbortSignal): Promise<any> => {
  const uri =  Env.pyxisAPIURL
    .clone()
    .setPath(`${Env.apiPrefix}/${Env.apiVersion}/projects/certification/requests/builds/id/${pid}/logs`);
  const params = abortSignal ? {'signal': abortSignal} : {};
  return getUri(uri, params);
}

/*
 *
 * Vendor APIs
 *
 */

// GETs vendor details by Org id
export const getVendorDetailsByOrgId = (orgId: string, abortSignal?: AbortSignal): Promise<any> => {
  const uri =  Env.pyxisAPIURL
      .clone()
      .setPath(`${Env.apiPrefix}/${Env.apiVersion}/vendors/org-id/${orgId}`);
  const params = abortSignal ? {signal: abortSignal} : {};
  return getUri(uri, params);
}

// Updates a vendor by its vendorId
export function updateVendor(vendorData): Promise<any> {
  const uri = Env.pyxisAPIURL
      .clone()
      .setPath(`${Env.apiPrefix}/${Env.apiVersion}/vendors/id/${vendorData[VendorKeys.id]}`);
  return patchUri(uri, vendorData);
}

// GETs the repository by ISV PID
export const getRepositoryByPID = (url: string, signal?: AbortSignal): Promise<IRepoDetailsResponse> => {
  const uri =  Env.pyxisAPIURL
    .clone()
    .setPath(`${Env.apiPrefix}${url}`);
  const params = signal ? {'signal': signal} : {};
  return getUri(uri, params);
}

/*
 *
 * GraphQL APIs
 *
 */

//GETs the operator bundles by isv_pid
export const getOperatorBundles = (isvPID): Promise<any> => {

  const OperatorBundlesQuery = `query findOperatorPackages($isvPID: String) {
    find_operator_packages(filter: {association: {eq: $isvPID}}) {
      page
      total
      data {
        _id
        package_name
        source
        edges {
          operator_bundle(page: 0, page_size: 250) {
            page
            total
            data {
              _id
              channel_name
              csv_name
              latest_in_channel
              package
              version
              ocp_version
              skips
              skip_range
              replaces
            }
          }
        }
      }
    }
  }`;

  return postUri(Env.pyxisGraphqlAPIURL, { query: OperatorBundlesQuery, variables: { isvPID } });
}

//GET vulnerabilities per image by project
export const getVulnerabilities = (isvPID): Promise<any> => {

  const vulnerabilityQuery = `query findImages($isvPID: String) {
    find_images( page_size: 250, filter: {isv_pid: { eq: $isvPID }}) {
        total
        data {
            _id
            isv_pid
            edges {
                vulnerabilities {
                    total
                }
            }
        }
    }
}`;

  return postUri(Env.pyxisGraphqlAPIURL, { query: vulnerabilityQuery, variables: { isvPID } });
}

// GETs the test results for a container image by image ID
export const getTestResultByImageId = (imageId: string, abortSignal?: AbortSignal): Promise<IImageScanResultsResponse> => {
  const uri =  Env.pyxisAPIURL
    .clone()
    .setPath(`${Env.apiPrefix}/${Env.apiVersion}/images/id/${imageId}/test-results`);
  addQueryParamsToUri(uri, {
    sort_by: ['last_update_date[desc]']
  })
  addQueryParamsToUri(uri, {
    page: 0,
    page_size: 1
  });
  const params = abortSignal ? {'signal': abortSignal} : {};
  return getUri(uri, params);
}


// GETs the artifacts for a container image by image ID
export const getArtifactsByImageId = (imageId: string, abortSignal?: AbortSignal): Promise<IImageArtifacts> => {
  const uri = Env.pyxisAPIURL
    .clone()
    .setPath(`${Env.apiPrefix}/${Env.apiVersion}/images/id/${imageId}/artifacts`);
  addQueryParamsToUri(uri, {
    page: 0,
    page_size: 250,
    sort_by: "creation_date[desc]"
  });
  const params = abortSignal ? {'signal': abortSignal} : {};
  return getUri(uri, params);
}

// GETs the attachable products for a project Listing
export const getAttachableProducts = (apiUrlWithFilterQuery: string, abortSignal?: AbortSignal): Promise<any> => {
  const params = abortSignal ? {'signal': abortSignal} : {};
  return getUri(apiUrlWithFilterQuery + `&page_size=500&page=0`, params);
}

// POSTs a Product Listing
export function createProductListing(productListingData, abortSignal?: AbortSignal): Promise<any> {
  const uri =  Env.pyxisAPIURL
    .clone()
    .setPath(`${Env.apiPrefix}/${Env.apiVersion}/product-listings`);

  if (abortSignal) {
    // this will be coming from useFetch hook
    Object.assign(AdditionalParams, {'signal': abortSignal})
  }

  return postUri(uri, productListingData, AdditionalParams);
}

//UPDATE a product listing by its ID
export function patchProductListing(productListingData): Promise<any> {
  const uri = Env.pyxisAPIURL
    .clone()
    .setPath(`${Env.apiPrefix}/${Env.apiVersion}/product-listings/id/${productListingData._id}`);
  return patchUri(uri, productListingData);
}

//GET image vulnerabilities
export function fetchVulnerabilities(imageId: string): Promise<IVulnerabilitiesResponse> {
  const uri = Env.pyxisAPIURL.clone().setPath(`${Env.apiPrefix}/${Env.apiVersion}/images/id/${imageId}/vulnerabilities`);
  addQueryParamsToUri(uri, {
    page_size: 250
  });
  return getUri(uri);
}

//GET Image RPMs by Image ID
export function fetchRpmsByImageId(imageId: string): Promise<IRpmManifestsResponse> {
  const uri = Env.pyxisAPIURL.clone().setPath(`${Env.apiPrefix}/${Env.apiVersion}/images/id/${imageId}/rpm-manifest`);
  return getUri(uri);
}

//GET Image by ID
export function fetchImageByID(imageId: string): Promise<IPyxisImage> {
  const uri = Env.pyxisAPIURL.clone().setPath(`${Env.apiPrefix}/${Env.apiVersion}/images/id/${imageId}`);
  return getUri(uri);
}

//GET decrypted project's registry credentials
export function fetchProjectRegistryCredentials(projectId: string, abortSignal?: AbortSignal): Promise<IProjectSecrets> {
  const uri = Env.pyxisAPIURL.clone().setPath(`${Env.apiPrefix}/${Env.apiVersion}/projects/certification/id/${projectId}/secrets`);
  const params = abortSignal ? {'signal': abortSignal} : {};
  return getUri(uri, params);
}


//Create image requests for Delete/Sync Tags/Publish/Unpublish
export function createImageRequest(projectId: string, payload): Promise<any> {
  const uri = Env.pyxisAPIURL.clone().setPath(`${Env.apiPrefix}/${Env.apiVersion}/projects/certification/id/${projectId}/requests/images`);
  return postUri(uri, payload);
}

const CommonImageGraphQLKeys = `
      _id
      isv_pid
      creation_date
      architecture
      image_id
      certified
      repositories {
        manifest_list_digest
        published
        tags {
          name
          added_date
        }
      }
      container_grades {
        status,
        status_message,
        exception_approved
      }
      freshness_grades {
        start_date
        end_date
        grade
      }
      edges {
        vulnerabilities {
          total
        }
        requests {
          data {
            operation
            status
            status_message
          }
        }
        test_results(sort_by: {
          field: "creation_date",
          order: DESC
        }) {
          data {
            passed
            _id
            creation_date
            results {
              failed {
                name
                description
              }
              passed {
                name
                description
              }
              warning {
                name
                description
              }
              errors {
                name
                description
              }
            }
          }
        }
      }
`;

const getCombinedQuery = (status: 'published' | 'unpublished' | `all`, searchQuery: string, hasManifestFilter?: boolean) => {
  searchQuery = searchQuery
    .replace(/"/g, '\\"')
    .replace(/[.*+?^${}()|[\]\\]/g, '\\\\$&');
  const manifestFilter = hasManifestFilter ? "{repositories: {manifest_list_digest: {eq: null}}}" : "";
  const statusQuery = status !== 'all' ?
    `${status === 'published' ?
      `{ repositories: { published: { eq: true } } }` :
      `{ repositories: { published: { ne: true } } }`}`
    : '';
  return searchQuery ? `
  or: [
        {
          and: [
            { deleted: { ne: true } }
            { repositories: { tags: { name: { iregex: "${searchQuery}" } } } }
            ${manifestFilter}
            ${statusQuery}
          ]
        }
        {
          and: [
            { deleted: { ne: true } }
            { image_id: { iregex: "${searchQuery}" } }
            ${manifestFilter}
            ${statusQuery}
          ]
        }
      ]
  ` : `
  and: [
         { deleted: { ne: true } }
         ${manifestFilter}
         ${statusQuery}
      ]
  `;
}

//GET paginated images by project's ISV PID
export const getImagesByProjectId = (
  projectId: string,
  paginationObj: IPageState,
  status: 'published' | 'unpublished' | `all`,
  searchQuery: string,
  sortObject?: any,
  abortSignal?: AbortSignal): Promise<IImageListGraphQLResponse> => {
  const combinedFilterQuery = getCombinedQuery(status, searchQuery, true);
  const imagesQuery = `query findCertProjectImages(
  $projectId: ObjectIDFilterScalar
  $pageNumber: Int
  $pageSize: Int
) {
  imageList: find_certification_projects_images(
    id: $projectId
    page_size: $pageSize
    page: $pageNumber
    sort_by: { field: "${sortObject.field}", order: ${sortObject.order} }
    filter: {
     ${combinedFilterQuery}
    }
  ) {
    total
    error {
      status
      detail
    }
    data {
      ${CommonImageGraphQLKeys}
    }
  }
}`;
  const params = abortSignal ? {'signal': abortSignal} : {};
  return postUri(Env.pyxisGraphqlAPIURL, {
    query: imagesQuery, variables: {
      projectId, pageNumber: paginationObj.page - 1, pageSize: paginationObj.perPage
    },
  }, params);
}

//GET multi arch image list
export const getMultiArchListByProjectId = (
  projectId: string,
  paginationObj: IPageState,
  status: 'published' | 'unpublished' | `all`,
  searchQuery: string,
  abortSignal: AbortSignal
): Promise<GQLMultiArchListResponse> => {
  const combinedFilterQuery = getCombinedQuery(status, searchQuery);
  const multiArchQuery = `query getMultiArchList($projectId: ObjectIDFilterScalar, $pageNumber: Int, $pageSize: Int) {
  find_certification_projects_images_by_digest(
    id: $projectId
    page_size: $pageSize
    page: $pageNumber
    sort_by: {field: "creation_date", order: DESC}
    filter: {
     ${combinedFilterQuery}
    }
  ) {
    data {
      images {
        ${CommonImageGraphQLKeys}
      }
      manifest_list_digest
    }
    error {
      status
      detail
    }
    total
  }
}`;
  const params = abortSignal ? {'signal': abortSignal} : {};
  return postUri(Env.pyxisGraphqlAPIURL, {
    query: multiArchQuery, variables: {
      projectId, pageNumber: paginationObj.page - 1, pageSize: paginationObj.perPage
    },
  }, params);
}


//GET initial data of the project image list to check if it is multi arch or not
export const fetchInitialImagesData = (
  projectId: string,
  ospId: string,
  abortSignal?: AbortSignal): Promise<{
  data: {
    imageListWithManifests: any,
    imageListWithoutManifests: any
    repoDetails: any
  }
}> => {
  const imagesQuery = `query initialImageData($projectId: ObjectIDFilterScalar, $pageNumber: Int, $pageSize: Int,  $ospId: String) {
  imageListWithManifests: find_certification_projects_images(
    id: $projectId
    page_size: $pageSize
    page: $pageNumber
    filter: {and: [{deleted: {ne: true}}, {repositories: {manifest_list_digest: {ne: null}}}]}
  ) {
    error {
      status
      detail
    }
    data {
      _id
    }
  }
  imageListWithoutManifests: find_certification_projects_images(
    id: $projectId
    page_size: $pageSize
    page: $pageNumber
    filter: {and: [{deleted: {ne: true}}, {repositories: {manifest_list_digest: {eq: null}}}]}
  ) {
    error {
      status
      detail
    }
    data {
      _id
    }
  }
  repoDetails: find_repositories(
    filter: {
      isv_pid: {
        eq: $ospId
      }
    }
  ) {
    data {
      _id
    }
  }
}`;
  const params = abortSignal ? {'signal': abortSignal} : {};
  return postUri(Env.pyxisGraphqlAPIURL, {
    query: imagesQuery, variables: {
      projectId, pageNumber: 0, pageSize: 1, ospId
    },
  }, params);
}


export const batchUpdateProductDetails = (productDetails: any[]): Promise<any> => {
  const batchUpdateMutation = mutation(productDetails.map((item, index)=> (
    {
      operation: {
        name: 'update_product_listing',
        alias: `productUpdate${index}`
      },
      variables: {
        ['id' + (index)]: {
          name: "id",
          type: 'ObjectIDFilterScalar',
          value: item[ProductKeys.id],
          required: true
        },
        ['input' + (index)]: {
          name: "input",
          type: 'ProductListingInput',
          value: item,
          required: true
        }
      },
      fields: [{data: [ProductKeys.id]}, {error: ['detail', 'status']}]
    }
  )));
  return postUri(Env.pyxisGraphqlAPIURL, batchUpdateMutation);
}

// GETs a Product Listing details by ID
export function getProductListingById(id: string, abortSignal?: AbortSignal): Promise<any> {
  const uri = Env.pyxisAPIURL.clone()
    .setPath(`${Env.apiPrefix}/${Env.apiVersion}/product-listings/id/${id}`);

  if (abortSignal) {
    Object.assign(AdditionalParams, {'signal': abortSignal})
  }
  return getUri(uri, AdditionalParams);
}

export function getAttachedProductsById(id: string, pagination: IPaginationRequest, extraParams: IAPIParams = {}, abortSignal?: AbortSignal): Promise<any> {
  const uri = Env.pyxisAPIURL.clone()
    .setPath(`${Env.apiPrefix}/${Env.apiVersion}/projects/certification/id/${id}/product-listings`);
  addQueryParamsToUri(uri, pagination);
  addQueryParamsToUri(uri, extraParams);
  if (abortSignal) {
    Object.assign(AdditionalParams, { "signal": abortSignal });
  }
  return getUri(uri, AdditionalParams);
}

export const fetchAttachableProducts = (orgId: string, pagination: IPaginationRequest, extraParams = {}, abortSignal?: AbortSignal): Promise<any> => {
  const uri = Env.pyxisAPIURL
    .clone()
    .setPath(`${Env.apiPrefix}/${Env.apiVersion}/vendors/org-id/${orgId}/product-listings`);
  addQueryParamsToUri(uri, extraParams);
  addQueryParamsToUri(uri, pagination);
  const params = abortSignal ? { "signal": abortSignal } : {};
  return getUri(uri, params);
};

